import React from 'react';
import { StyleSheet, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';

import { Main } from '@expo/html-elements';

import { useA11ySelector } from '@sg/a11y-helpers';
import { theme, useResponsive, webOnlyStyles } from '@sg/garnish';

import { Home } from '@order/features/home';
import { NAVBAR_HEADER_HEIGHT } from '@order/navigation';
import { useUsableNetBannerHeight } from '@order/usable-net';

import { AppFooter, RecentOrdersRail } from '../../../../components';
import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { type useHomeContent } from '../../hooks';
import { HomeScreenBanner } from '../HomeScreenBanner';

export const HomeScreenContentSM = (props: HomeScreenContentPropsSM) => {
  const {
    homeContent,
    hasPlacedOrder,
    ctaPressed,
    secondaryCtaLabel,
    secondaryCtaPressed,
    shouldRenderSecondaryCta,
  } = props;

  const a11ySelector = useA11ySelector('home.container');
  const contentA11ySelector = useA11ySelector('home.hero.text-content');
  const imageA11ySelector = useA11ySelector('home.hero.img');

  // ─── Flags ───────────────────────────────────────────────────────────

  const isLoggedIn = useIsLoggedIn();
  const hasReorderRail = isLoggedIn && hasPlacedOrder;

  // ─── Main Content Height ─────────────────────────────────────────────
  // This calculates the available height due to being outside the scroll view.

  const usableNetBannerHeight = useUsableNetBannerHeight();

  // ─── Content Height ───────────────────────────────────────────────────
  // For when the reorder rail is not present.
  // On SM: Footer will peek above fold but not entirely.
  // On LG: Footer is intentionally below the fold.

  const { match } = useResponsive();

  const fullContentHeight = webOnlyStyles({
    minHeight: `calc(100svh - ${NAVBAR_HEADER_HEIGHT}px - ${usableNetBannerHeight}px)` as never,
  });

  const mainContentStyles = match([
    undefined,
    hasReorderRail ? fullContentHeight : partialContentHeight,
    fullContentHeight,
  ]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ScrollView contentContainerStyle={styles.container} {...a11ySelector.props}>
      <Main style={mainContentStyles as never}>
        <HomeScreenBanner />

        <Home.ContainerSM>
          <Home.ColumnsContainerSM>
            <Home.ColumnLeftSM {...contentA11ySelector.props}>
              <Home.EyebrowText
                text={homeContent.eyebrowText}
                fontColor={homeContent.eyebrowTextColorSM}
              />

              <Home.HeroText text={homeContent.heroText} fontColor={homeContent.heroTextColorSM} />

              {homeContent.infoText ? (
                <Home.InfoText
                  text={homeContent.infoText}
                  fontColor={homeContent.infoTextColorSM}
                />
              ) : null}

              <View style={styles.ctaContainer}>
                <Home.HeaderCta
                  accessibilityLabel={homeContent.ctaText}
                  deepLink={homeContent.ctaDeepLink}
                  onPressCta={ctaPressed}
                >
                  {homeContent.ctaText}
                </Home.HeaderCta>

                {shouldRenderSecondaryCta ? (
                  <Home.HeaderCta
                    accessibilityLabel={secondaryCtaLabel}
                    onPressCta={secondaryCtaPressed}
                  >
                    {secondaryCtaLabel}
                  </Home.HeaderCta>
                ) : null}
              </View>
            </Home.ColumnLeftSM>

            <Home.HeroImageSM
              imageUrl={homeContent.mainImageSM}
              imageA11yLabel={homeContent.mainImageA11ySM}
              {...imageA11ySelector.props}
            />

            <Home.HomeHeroImagePlaceholderSM />
          </Home.ColumnsContainerSM>
        </Home.ContainerSM>

        {hasReorderRail ? <RecentOrdersRail shouldRefetchOnFocus /> : null}
      </Main>

      <AppFooter />
    </ScrollView>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
  },
  ctaContainer: {
    flexDirection: 'row',
    gap: theme.spacing['4'],
  },
});

// On SM, if the reorder rail is not present, the footer should be partially visible.
const partialContentHeight = webOnlyStyles({
  height: '75svh' as never,
});

// ─── Types ───────────────────────────────────────────────────────────

type HomeScreenContentPropsSM = {
  homeContent: ReturnType<typeof useHomeContent>['homeContent'];
  hasPlacedOrder: boolean;
  ctaPressed: () => void;
  secondaryCtaLabel: string;
  secondaryCtaPressed: () => void;
  shouldRenderSecondaryCta: boolean;
};
